import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useGetRallyById,
  useUpdateRallyMutation,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useGetAllRoutes } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { useGetAllTypes } from "../../../../../api/jeep-rally/types/types.hooks";
import { normalizeCategories } from "../../Categories/utils/category.utils";
import { normalizeRoutes } from "../../Routes/utils/route.utils";
import { normalizeTypes } from "../../Types/utils/type.utils";
import RallyForm from "../_components/RallyForm";
import {
  buildRallyPayload,
  getRallyApiErrorMessage,
  initialRallyForm,
  mapRallyToForm,
  normalizeRally,
  validateRallyForm,
} from "../utils/rally.utils";

const RallyUpdatePage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [form, setForm] = useState(initialRallyForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: rallyData,
    isLoading: isRallyLoading,
    isError: isRallyError,
    error: rallyError,
  } = useGetRallyById(id);

  const {
    mutateAsync: updateRally,
    isLoading,
    isPending,
    reset,
  } = useUpdateRallyMutation();

  const submitting = isLoading || isPending;

  const {
    data: routesData,
    isLoading: isRoutesLoading,
    isError: isRoutesError,
  } = useGetAllRoutes({ status: "active" });
  const routes = useMemo(() => normalizeRoutes(routesData), [routesData]);

  const {
    data: typesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetAllTypes({ status: "active" });
  const types = useMemo(() => normalizeTypes(typesData), [typesData]);

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetAllCategories({ status: "active" });
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  useEffect(() => {
    const rally = normalizeRally(rallyData);
    if (!rally) return;
    setForm(mapRallyToForm(rally));
  }, [rallyData]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    reset();
    setSubmitError("");

    const nextErrors = validateRallyForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await updateRally({
        rallyId: id,
        rallyData: buildRallyPayload(form, { isEdit: true }),
      });
      history.push("/jeep-rally/rallies");
    } catch (error) {
      setSubmitError(
        getRallyApiErrorMessage(
          error,
          "Failed to update rally. Please try again."
        )
      );
    }
  };

  const goBack = () => history.push("/jeep-rally/rallies");

  if (isRallyLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isRallyError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getRallyApiErrorMessage(
                rallyError,
                "Failed to load rally details."
              )}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rallies list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Update Rally</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Update route, types, categories with pricing, and other rally
            details. Included content fields fully replace existing values.
          </p>

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          <RallyForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Update Rally"
            submittingLabel="Updating..."
            isEdit
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
            routes={routes}
            isRoutesLoading={isRoutesLoading}
            isRoutesError={isRoutesError}
            types={types}
            isTypesLoading={isTypesLoading}
            isTypesError={isTypesError}
            categories={categories}
            isCategoriesLoading={isCategoriesLoading}
            isCategoriesError={isCategoriesError}
          />
        </div>
      </div>
    </div>
  );
};

export default RallyUpdatePage;
